import React, { useEffect, useMemo, useState } from "react";
import { MdChevronLeft, MdChevronRight, MdTrendingUp, MdTrendingDown } from "react-icons/md";
import { parseISO, isValid, format, addMonths, parse as parseFmt } from "date-fns";

/**
 * Teacher Performance Leaderboard — Single Table with Tier Dividers + Loading
 * --------------------------------------------------------------------------
 * - Tiers by Active Students:
 *   Lead (>=15), Elite (12–14), Pro (10–11), Associate (<10)
 * - Sorted within each tier by % Completed desc (nulls last), then Taught desc, then Name asc.
 * - Uses Timesheets + StudentTwo (join on familyWaveId ↔ waveCustomerId) for Active/Expected/Taught/%.
 * - Uses Cancellations for teacher/student cancels and Δ vs previous month (by dateTime month, not excused).
 * - Shows "Loading Leaderboard…" until all data sources are ready.
 */

// ---------- date helpers ----------
const monthKeyFromDateTime = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : parseISO(String(value));
  if (!isValid(d)) return null;
  return format(d, "yyyy-MM");
};

const parseMonth = (ym) => parseFmt(ym, "yyyy-MM", new Date());
const prettyMonth = (ym) => format(parseMonth(ym), "MMMM yyyy");
const shiftMonth = (ym, delta) => format(addMonths(parseMonth(ym), delta), "yyyy-MM");

// Parse timesheet monthYear like "September/2025" -> "yyyy-MM"
const timesheetMonthKey = (monthYear) => {
  if (!monthYear) return null;
  const raw = String(monthYear).trim();
  let d = parseFmt(raw, "MMMM/yyyy", new Date());
  if (!isValid(d)) d = parseFmt(raw.replace(/\s+/g, ""), "MMMM/yyyy", new Date()); // tolerate "September /2025"
  if (!isValid(d)) return null;
  return format(d, "yyyy-MM");
};

const norm = (s) => (s == null ? "" : String(s).trim());
const normSchool = (s) => norm(s).toUpperCase();
const normWave = (s) => norm(s).toLowerCase(); // case-insensitive compare for wave ids

// Tier helpers
const getTierName = (active) =>
  active >= 15 ? "Lead" : active >= 12 ? "Elite" : active >= 10 ? "Pro" : "Associate";

const tierStyles = {
  Lead: "from-yellow-400 via-amber-500 to-orange-500",
  Elite: "from-indigo-400 via-purple-500 to-fuchsia-500",
  Pro: "from-emerald-400 via-green-500 to-teal-500",
  Associate: "from-sky-400 via-cyan-500 to-blue-500",
};

export default function TeacherTieredLeaderboard({
  cancellations: cancellationsProp,      // cancellation docs array
  timesheets,                           // teacherTimesheetSchema docs array
  studentFamilies,                      // StudentTwo docs array

  // Optional built-in fetch of cancellations (if you don't pass cancellationsProp)
  fetchUrl,

  // NEW: external loading flags from parent (use if you load these asynchronously)
  loadingTimesheets,
  loadingStudentFamilies,
  loadingCancellations,

  defaultMonth,
  schoolFilterEnabled = true,
  title = "Teacher Performance Leaderboard"
}) {
  // Built-in cancellation fetching (if fetchUrl given and no array supplied)
  const [cancellationsState, setCancellationsState] = useState([]);
  const cancellations = Array.isArray(cancellationsProp) ? cancellationsProp : cancellationsState;
  const [loadingCancelsInternal, setLoadingCancelsInternal] = useState(!!fetchUrl && !Array.isArray(cancellationsProp));
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    async function load() {
      if (!fetchUrl || Array.isArray(cancellationsProp)) return;
      setLoadingCancelsInternal(true);
      setError(null);
      try {
        const res = await fetch(fetchUrl);
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        const data = await res.json();
        if (!alive) return;
setCancellationsState(Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []));
      } catch (e) {
        if (alive) setError(String(e?.message || e));
      } finally {
        if (alive) setLoadingCancelsInternal(false);
      }
    }
    load();
    return () => { alive = false; };
  }, [fetchUrl, cancellationsProp]);



  // Compose loading state: internal fetch OR parent-provided flags
  const loadingAll =
    loadingCancelsInternal ||
    loadingTimesheets ||
    loadingStudentFamilies ||
    loadingCancellations;

  // WaveID -> StudentTwo family
  const famByWaveId = useMemo(() => {
    const m = new Map();
    for (const f of studentFamilies || []) {
      const wave = normWave(f?.waveCustomerId);
      if (wave) m.set(wave, f);
    }
    return m;
  }, [studentFamilies]);

  // Build months from cancellations + timesheets
  const months = useMemo(() => {
    const set = new Set();
    for (const c of cancellations || []) {
      const mk = monthKeyFromDateTime(c?.dateTime);
      if (mk) set.add(mk);
    }
    for (const ts of timesheets || []) {
      const mk = timesheetMonthKey(ts?.monthYear);
      if (mk) set.add(mk);
    }
    return Array.from(set).sort();
  }, [cancellations, timesheets]);

  // Selected month
  const currentYMD = monthKeyFromDateTime(new Date());
  const previousYMD = shiftMonth(currentYMD, -1);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    if (defaultMonth && months.includes(defaultMonth)) return defaultMonth;
    if (defaultMonth && months.length === 0) return defaultMonth;
    if (months.includes(previousYMD)) return previousYMD;
    if (months.includes(currentYMD)) return currentYMD;
    return months[months.length - 1] || previousYMD || currentYMD;
  });
  useEffect(() => {
    if (!months.length || selectedMonth) return;
    const fallback = months.includes(previousYMD)
      ? previousYMD
      : (months.includes(currentYMD) ? currentYMD : months[months.length - 1]);
    setSelectedMonth(fallback);
  }, [months, selectedMonth, previousYMD, currentYMD]);

  // School filter
  const schools = useMemo(() => {
    const s = new Set();
    for (const c of cancellations || []) if (c?.school) s.add(normSchool(c.school));
    for (const ts of timesheets || []) if (ts?.school) s.add(normSchool(ts.school));
    for (const ts of timesheets || []) {
      const fams = Array.isArray(ts?.families) ? ts.families : [];
      for (const fam of fams) if (fam?.school) s.add(normSchool(fam.school));
    }
    for (const f of studentFamilies || []) if (f?.school) s.add(normSchool(f.school));
    return Array.from(s).sort();
  }, [cancellations, timesheets, studentFamilies]);

  const [school, setSchool] = useState(""); // empty = all

  // Teacher meta from cancels (avatars if available)
  const teacherMetaFromCancels = useMemo(() => {
    const map = new Map();
    for (const c of cancellations || []) {
      const key = c?.teacherId || c?.teacherName || "unknown";
      if (!map.has(key)) {
        map.set(key, {
          teacherId: c?.teacherId || null,
          teacherName: c?.teacherName || "Unknown",
          teacherImageUrl: c?.teacherImageUrl || "",
          school: c?.school || ""
        });
      }
    }
    return map;
  }, [cancellations]);

useEffect(() => {
   if (!months.length) return;
   if (!selectedMonth || !months.includes(selectedMonth)) {
     const fallback = months.includes(previousYMD)
       ? previousYMD
       : (months.includes(currentYMD) ? currentYMD : months[months.length - 1]);
     setSelectedMonth(fallback);
   }
 }, [months, selectedMonth, previousYMD, currentYMD]); 

  // Timesheet aggregation for selected month
  const timesheetAgg = useMemo(() => {
    const agg = new Map(); // key -> { teacherId, teacherName, school, activeSet:Set, taught:number }
    for (const ts of timesheets || []) {
      const mk = timesheetMonthKey(ts?.monthYear);
      if (!mk || mk !== selectedMonth) continue;

      const tId = ts?.teacherId || null;
      const tName = ts?.teacherName || "Unknown";
      const tsSchool = normSchool(ts?.school);

      const families = Array.isArray(ts?.families) ? ts.families : [];
      for (const fam of families) {
        const famSchool = normSchool(fam?.school || tsSchool);
        if (school && famSchool !== normSchool(school)) continue;

        const key = tId || tName || "unknown";
        const rec = agg.get(key) || {
          teacherId: tId,
          teacherName: tName,
          school: famSchool || tsSchool || "",
          activeSet: new Set(),
          taught: 0
        };

        // Taught lessons
        const lessons = Array.isArray(fam?.lessons) ? fam.lessons : [];
        rec.taught += lessons.length;

        // Active students via StudentTwo join
        const wave = normWave(fam?.familyWaveId);
        const studentTwoFam = wave ? famByWaveId.get(wave) : null;

        if (studentTwoFam && Array.isArray(studentTwoFam.students)) {
          for (const stu of studentTwoFam.students) {
            const status = String(stu?.studentStatus || "").toLowerCase();
            if (status === "inactive") continue;
            if ((stu?.teachersId || "").trim() !== (tId || "").trim()) continue;
            if (school && normSchool(studentTwoFam?.school) !== normSchool(school)) continue;

            const sid = stu?.studentId || `${(stu?.studentName || "").trim()}::${(tId || "").trim()}`;
            if (sid) rec.activeSet.add(sid);
          }
        } else {
          // Fallback: infer active students from distinct student names on lessons
          const names = new Set();
          for (const l of lessons) {
            const nm = (l?.studentName || "").trim();
            if (nm) names.add(`${nm}::${(tId || "").trim()}`);
          }
          for (const sid of names) rec.activeSet.add(sid);
        }

        agg.set(key, rec);
      }
    }
    return agg;
  }, [timesheets, selectedMonth, school, famByWaveId]);

  // Cancellations (selected month, not excused)
  const teacherCancelsArr = useMemo(() => {
    return (cancellations || []).filter((c) => {
      if (c?.excusedByAdmin) return false;
      if (c?.teacherCancelled !== true) return false;
      const mk = monthKeyFromDateTime(c?.dateTime);
      if (!mk || (selectedMonth && mk !== selectedMonth)) return false;
      if (school && normSchool(c?.school) !== normSchool(school)) return false;
      return true;
    });
  }, [cancellations, selectedMonth, school]);

  const studentCancelsArr = useMemo(() => {
    return (cancellations || []).filter((c) => {
      if (c?.excusedByAdmin) return false;
      if (c?.teacherCancelled !== false) return false;
      const mk = monthKeyFromDateTime(c?.dateTime);
      if (!mk || (selectedMonth && mk !== selectedMonth)) return false;
      if (school && normSchool(c?.school) !== normSchool(school)) return false;
      return true;
    });
  }, [cancellations, selectedMonth, school]);

  // Prev-month teacher cancels (for delta)
  const prevMonth = useMemo(() => shiftMonth(selectedMonth, -1), [selectedMonth]);
  const prevMonthMap = useMemo(() => {
    const map = new Map();
    for (const c of (cancellations || [])) {
      if (c?.excusedByAdmin) continue;
      if (c?.teacherCancelled !== true) continue;
      const mk = monthKeyFromDateTime(c?.dateTime);
      if (!mk || mk !== prevMonth) continue;
      if (school && normSchool(c?.school) !== normSchool(school)) continue;
      const key = c?.teacherId || c?.teacherName || "unknown";
      map.set(key, (map.get(key) || 0) + 1);
    }
    return map;
  }, [cancellations, prevMonth, school]);

  // Base rows (unsorted)
  const baseRows = useMemo(() => {
    const map = new Map();

    // Seed from timesheets
    for (const [key, rec] of timesheetAgg.entries()) {
      const meta = teacherMetaFromCancels.get(key) || {};
      const active = rec.activeSet.size;
      const expected = active * 4;
      const taught = rec.taught || 0;
      const pctCompleted = expected > 0 ? Math.min(taught, expected) / expected : null;

      map.set(key, {
        teacherId: rec.teacherId || meta.teacherId || null,
        teacherName: rec.teacherName || meta.teacherName || "Unknown",
        teacherImageUrl: meta.teacherImageUrl || "",
        school: rec.school || meta.school || (school || ""),
        activeStudents: active,
        expected,
        taught,
        pctCompleted,
        teacherCancels: 0,
        studentCancels: 0
      });
    }

    // Merge cancellations (ensures teachers with cancels but no timesheet still appear)
    for (const c of teacherCancelsArr) {
      const key = c?.teacherId || c?.teacherName || "unknown";
      const existing = map.get(key);
      const meta = teacherMetaFromCancels.get(key) || {};
      const row = existing || {
        teacherId: c?.teacherId || meta.teacherId || null,
        teacherName: c?.teacherName || meta.teacherName || "Unknown",
        teacherImageUrl: c?.teacherImageUrl || meta.teacherImageUrl || "",
        school: c?.school || meta.school || (school || ""),
        activeStudents: 0,
        expected: 0,
        taught: 0,
        pctCompleted: null,
        teacherCancels: 0,
        studentCancels: 0
      };
      row.teacherCancels += 1;
      map.set(key, row);
    }

    for (const c of studentCancelsArr) {
      const key = c?.teacherId || c?.teacherName || "unknown";
      const existing = map.get(key);
      const meta = teacherMetaFromCancels.get(key) || {};
      const row = existing || {
        teacherId: c?.teacherId || meta.teacherId || null,
        teacherName: c?.teacherName || meta.teacherName || "Unknown",
        teacherImageUrl: c?.teacherImageUrl || meta.teacherImageUrl || "",
        school: c?.school || meta.school || (school || ""),
        activeStudents: 0,
        expected: 0,
        taught: 0,
        pctCompleted: null,
        teacherCancels: 0,
        studentCancels: 0
      };
      row.studentCancels += 1;
      map.set(key, row);
    }

    return Array.from(map.values());
  }, [timesheetAgg, teacherCancelsArr, studentCancelsArr, teacherMetaFromCancels, school]);

  // Tiers + sorting
  const sortByPctThenTaughtThenName = (a, b) => {
    const ap = a.pctCompleted == null ? -1 : a.pctCompleted;
    const bp = b.pctCompleted == null ? -1 : b.pctCompleted;
    if (bp !== ap) return bp - ap;                                   // % Completed DESC
    if ((b.taught || 0) !== (a.taught || 0)) return (b.taught || 0) - (a.taught || 0); // Taught DESC
    return String(a.teacherName).localeCompare(String(b.teacherName));                  // Name ASC
  };

  const tieredRows = useMemo(() => {
    const groups = { Lead: [], Elite: [], Pro: [], Associate: [] };
    for (const r of baseRows) {
      const tier = getTierName(r.activeStudents || 0);
      groups[tier].push({ ...r, tier });
    }
    for (const k of Object.keys(groups)) groups[k].sort(sortByPctThenTaughtThenName);
    for (const k of Object.keys(groups)) {
      groups[k] = groups[k].map((r, i) => ({ ...r, tierRank: i + 1 }));
    }
    return groups;
  }, [baseRows]);

  // Combined single-table rows with divider entries
  const combinedRows = useMemo(() => {
    const arr = [];
    const order = ["Lead", "Elite", "Pro", "Associate"];
    for (const tier of order) {
      const list = tieredRows[tier];
      if (!list || list.length === 0) continue;
      arr.push({ __divider: true, tier });   // divider row
      arr.push(...list);
    }
    return arr;
  }, [tieredRows]);

  // Totals
  const totalTeacherCancels = combinedRows.reduce((s, r) => s + (r.__divider ? 0 : (r.teacherCancels || 0)), 0);
  const totalStudentCancels = combinedRows.reduce((s, r) => s + (r.__divider ? 0 : (r.studentCancels || 0)), 0);

  // Navigation
  const canGoPrev = months.length && months[0] < selectedMonth;
  const canGoNext = months.length && months[months.length - 1] > selectedMonth;
  const goPrev = () => canGoPrev && setSelectedMonth(shiftMonth(selectedMonth, -1));
  const goNext = () => canGoNext && setSelectedMonth(shiftMonth(selectedMonth, +1));

  // CSV export (includes Tier + Tier Rank)
  const fmtPct = (p) => (p == null ? "" : `${Math.round(p * 100)}%`);
  const downloadCSV = () => {
    const header = [
      "Tier","Tier Rank","Teacher","School",
      "Active Students","Expected Lessons","Taught Lessons","% Completed",
      "Teacher Cancels","Student Cancels"
    ].join(",");
    const lines = combinedRows
      .filter(r => !r.__divider)
      .map(r => [
        r.tier,
        r.tierRank,
        escapeCSV(r.teacherName),
        escapeCSV(r.school || ""),
        r.activeStudents,
        r.expected,
        r.taught,
        fmtPct(r.pctCompleted),
        r.teacherCancels,
        r.studentCancels || 0
      ].join(","));
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `teacher-tiered-leaderboard-${selectedMonth}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 3000);
  };

  function escapeCSV(v){
    if (v == null) return "";
    const s = String(v);
    if (s.includes(",") || s.includes("\"") || s.includes("\n")) return '"' + s.replace(/"/g, '""') + '"';
    return s;
  }

  // Delta chip (uses previous-month teacher cancels)
  const Delta = ({ teacherId, teacherName, teacherCancels }) => {
    const prev = prevMonthMap.get(teacherId || teacherName) || 0;
    const delta = (teacherCancels || 0) - prev;
    if (delta > 0) return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-200">
        <MdTrendingUp className="h-3.5 w-3.5" /> +{delta}
      </span>
    );
    if (delta < 0) return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
        <MdTrendingDown className="h-3.5 w-3.5" /> {delta}
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-200">
        0
      </span>
    );
  };

  // Simple spinner
  const Spinner = () => (
    <span className="inline-block h-10 w-10 animate-spin rounded-full border-[3px] border-gray-300 bg-blue-400 border-t-gray-600 align-middle" />

  );

  // ---- UI ----
  const COLSPAN = 10;

  return (
    <div className="w-11-12 max-w-7xl mx-auto p-4 sm:p-6">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">{title}</h2>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={goPrev} disabled={loadingAll || !canGoPrev} className="rounded-lg border px-2.5 py-2 text-gray-700 disabled:opacity-40">
            <MdChevronLeft className="h-5 w-5" />
          </button>
          <div className="min-w-[12rem] text-center font-medium text-gray-900 select-none">{prettyMonth(selectedMonth)}</div>
          <button onClick={goNext} disabled={loadingAll || !canGoNext} className="rounded-lg border px-2.5 py-2 text-gray-700 disabled:opacity-40">
            <MdChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Loading state */}
      {loadingAll && (
        <div className="rounded-2xl border shadow-sm p-8 flex items-center justify-center text-gray-700 gap-3">
          <Spinner />
          <span className="font-medium">Loading Leaderboard…</span>
        </div>
      )}

      {/* Error state */}
      {!loadingAll && error && (
        <div className="rounded-2xl border shadow-sm p-8 text-center text-red-600">{error}</div>
      )}

      {/* Empty state */}
      {!loadingAll && !error && combinedRows.length === 0 && (
        <div className="rounded-2xl border shadow-sm p-8 text-center text-gray-500">No data match the filters.</div>
      )}

      {/* Main table */}
      {!loadingAll && !error && combinedRows.length > 0 && (
        <div className="overflow-hidden rounded-2xl border shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left  text-xs font-semibold uppercase tracking-wide text-gray-600">Tier Rank</th>
                <th className="px-4 py-3 text-left  text-xs font-semibold uppercase tracking-wide text-gray-600">Teacher</th>
                <th className="px-4 py-3 text-left  text-xs font-semibold uppercase tracking-wide text-gray-600">School</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-600">Active</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-600">Expected</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-600">Taught</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-600">Completed</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-600">Teacher Cancels</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-600">Student Cancels</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-600">Δ vs Prev</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {combinedRows.map((r, idx) => {
                if (r.__divider) {
                  return (
                    <tr key={`divider-${r.tier}-${idx}`}>
                      <td colSpan={COLSPAN} className="px-0 py-0">
                        <div
                          className={`flex items-center justify-between px-4 py-0 text-white uppercase text-[10px] font-bold tracking-wider bg-gradient-to-r ${tierStyles[r.tier]} shadow-inner`}
                        >
                          <span>{r.tier}</span>
                          <span className="opacity-80">
                            {r.tier === "Lead" ? "15+ active students" :
                             r.tier === "Elite" ? "12–14 active students" :
                             r.tier === "Pro" ? "10–11 active students" : "< 10 active students"}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr key={(r.teacherId || r.teacherName) + "::" + idx} className="hover:bg-gray-50/60">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-200">
                          {r.tierRank}
                        </span>
                        {r.tier === "Lead" && r.tierRank <= 3 && (
                          <span className={r.tierRank === 1 ? "text-yellow-500" : r.tierRank === 2 ? "text-gray-500" : "text-amber-700"}>
                            {r.tierRank === 1 ? "🥇" : r.tierRank === 2 ? "🥈" : "🥉"}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {r.teacherImageUrl ? (
                          <img
                            src={r.teacherImageUrl[0] === 'h' ? r.teacherImageUrl : `https://app.atharo.app/images/${r.teacherImageUrl}`}
                            alt={r.teacherName}
                            className="h-9 w-9 rounded-full object-cover ring-1 ring-black/5"
                          />
                        ) : (
                          <div className="h-9 w-9 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center font-medium ring-1 ring-black/5">
                            {String(r.teacherName || "?").split(" ").map(p=>p[0]).join("").slice(0,2).toUpperCase()}
                          </div>
                        )}
                        <span className="font-medium text-gray-900">{r.teacherName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{r.school ? String(r.school).toUpperCase() : "—"}</td>
                    <td className="px-4 py-3 text-right text-gray-900">{r.activeStudents}</td>
                    <td className="px-4 py-3 text-right text-gray-900">{r.expected}</td>
                    <td className="px-4 py-3 text-right text-gray-900">{r.taught}</td>
                    <td className="px-4 py-3 text-right text-gray-900">{r.pctCompleted == null ? "—" : `${Math.round(r.pctCompleted * 100)}%`}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">{r.teacherCancels}</td>
                    <td className="px-4 py-3 text-right text-gray-900">{r.studentCancels || 0}</td>
                    <td className="px-4 py-3 text-right">
                      <Delta teacherId={r.teacherId} teacherName={r.teacherName} teacherCancels={r.teacherCancels} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan={COLSPAN} className="px-4 py-3 text-sm text-gray-600">
                  Totals this month — <span className="font-medium">Teacher cancels:</span> {totalTeacherCancels}
                  <span className="mx-2 text-gray-400">|</span>
                  <span className="font-medium">Student cancels:</span> {totalStudentCancels}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
